import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DomainIcon from '@mui/icons-material/Domain';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import SmsIcon from '@mui/icons-material/Sms';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SecurityIcon from '@mui/icons-material/Security';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SpeedIcon from '@mui/icons-material/Speed';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import GenericPage from '../../Commun/components/GenericPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSubscription } from '../hooks/useSubscription';
import { AcademySubscriptionPlan, BillingCycle } from '../types/academy';
import { SatimPaymentModal } from '../components/SatimPaymentModal';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

export const SubscriptionPage: React.FC = () => {
  const { t, dir } = useLanguage();
  const {
    usage,
    plans,
    invoices,
    loading,
    refreshUsage,
    isTrial,
    isExpired,
    remainingTrialDays,
  } = useSubscription();

  const [activeTab, setActiveTab] = useState<'usage' | 'plans'>('usage');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState<AcademySubscriptionPlan | null>(null);
  const [satimModalOpen, setSatimModalOpen] = useState(false);

  const handleOpenCheckout = (plan: AcademySubscriptionPlan) => {
    setSelectedPlanForPayment(plan);
    setSatimModalOpen(true);
  };

  const handlePaymentSuccess = (approvalCode: string) => {
    blobticMessage.success(
      t('subscription.successTitle', 'Abonnement activé !'),
      t(
        'subscription.successDesc',
        'Votre paiement SATIM a été validé avec succès (Réf: {0}). Vos quotas ont été mis à jour.',
        approvalCode
      )
    );
    refreshUsage();
    setSatimModalOpen(false);
    setActiveTab('usage');
  };

  // Helper for Quota percentages
  const getPercent = (used: number, max: number) => {
    if (max <= 0) return 10; // unlimited or 0
    return Math.min(100, Math.round((used / max) * 100));
  };

  const getProgressColor = (used: number, max: number): 'success' | 'warning' | 'error' | 'primary' => {
    if (max <= 0) return 'primary';
    const p = (used / max) * 100;
    if (p >= 90) return 'error';
    if (p >= 75) return 'warning';
    return 'success';
  };

  const currentPlanCode = usage?.planCode || 'FREE';

  return (
    <GenericPage
      title={t('subscription.pageTitle', 'Gestion de l’Abonnement & Facturation')}
      icon={<CardMembershipIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(_, val) => val && setActiveTab(val)}
            size="small"
            sx={{ bgcolor: 'background.paper' }}
          >
            <ToggleButton value="usage" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}>
              <SpeedIcon sx={{ fontSize: 18, mr: 1 }} />
              {t('subscription.tabUsage', 'Consommation & Quotas')}
            </ToggleButton>
            <ToggleButton value="plans" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}>
              <WorkspacePremiumIcon sx={{ fontSize: 18, mr: 1 }} />
              {t('subscription.tabPlans', 'Nos Forfaits & Tarifs')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            size="small"
            onClick={() => refreshUsage()}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('common.refresh', 'Actualiser')}
          </Button>
        </Stack>
      }
    >
      {loading && !usage ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('subscription.loading', 'Chargement des détails de l’abonnement...')}
          </Typography>
        </Box>
      ) : (
        <Box dir={dir} sx={{ pb: 4 }}>
          {/* TAB 1: USAGE & BILLING OVERVIEW */}
          {activeTab === 'usage' && (
            <Stack spacing={3}>
              {/* Active Plan Hero Card */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.15)',
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Chip
                        label={usage?.planBadge || usage?.planName || 'Plan'}
                        size="small"
                        icon={<StarIcon sx={{ color: '#f59e0b !important', fontSize: 16 }} />}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: '#fff',
                          fontWeight: 'bold',
                        }}
                      />
                      {isTrial && (
                        <Chip
                          label={t('subscription.status.trial', 'Essai Gratuit ({0} jours restants)', remainingTrialDays)}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                      {usage?.status === 'active' && (
                        <Chip
                          label={t('subscription.status.active', 'Abonnement Actif')}
                          size="small"
                          color="success"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                      {isExpired && (
                        <Chip
                          label={t('subscription.status.expired', 'Abonnement Expiré')}
                          size="small"
                          color="error"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>

                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, letterSpacing: -0.5 }}>
                      Forfait {usage?.planName || 'Gratuit'}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, maxWidth: 600 }}>
                      Cycle de facturation :{' '}
                      <b>{usage?.billingCycle === 'yearly' ? t('subscription.yearly', 'Annuel (-20%)') : t('subscription.monthly', 'Mensuel')}</b>
                      {usage?.endDate && ` • Prochain renouvellement le ${usage.endDate}`}
                      {usage?.paymentMethod && ` • Moyen : ${usage.paymentMethod}`}
                    </Typography>

                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="contained"
                        onClick={() => setActiveTab('plans')}
                        startIcon={<UpgradeIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                          color: '#fff',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          px: 3,
                          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                          },
                        }}
                      >
                        {t('subscription.changePlan', 'Changer ou Mettre à niveau')}
                      </Button>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                        {t('subscription.securedBy', 'Paiements sécurisés via')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <SecurityIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#f8fafc' }}>
                          Passerelle Nationale SATIM (CIB & Edahabia)
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Quotas & Usage Gauges */}
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  {t('subscription.quotasTitle', 'Quotas & Utilisation en Direct')}
                </Typography>

                <Grid container spacing={2.5}>
                  {/* Students */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#eff6ff', color: '#2563eb' }}>
                              <SchoolIcon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('subscription.quota.students', 'Apprenants')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('subscription.quota.studentsSub', 'Inscrits dans l’académie')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            {usage?.studentsUsed || 0}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              / {usage?.studentsMax === -1 ? '∞' : usage?.studentsMax}
                            </Typography>
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={getPercent(usage?.studentsUsed || 0, usage?.studentsMax || 0)}
                          color={getProgressColor(usage?.studentsUsed || 0, usage?.studentsMax || 0)}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{usage?.studentsMax === -1 ? 'Illimité' : `${getPercent(usage?.studentsUsed || 0, usage?.studentsMax || 0)}% utilisé`}</span>
                          {usage?.studentsMax !== -1 && usage?.studentsMax! - (usage?.studentsUsed || 0) > 0 && (
                            <span>{usage!.studentsMax - (usage?.studentsUsed || 0)} places restantes</span>
                          )}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Teachers */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#f0fdf4', color: '#16a34a' }}>
                              <PersonIcon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('subscription.quota.teachers', 'Enseignants')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('subscription.quota.teachersSub', 'Formateurs actifs')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            {usage?.teachersUsed || 0}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              / {usage?.teachersMax === -1 ? '∞' : usage?.teachersMax}
                            </Typography>
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={getPercent(usage?.teachersUsed || 0, usage?.teachersMax || 0)}
                          color={getProgressColor(usage?.teachersUsed || 0, usage?.teachersMax || 0)}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                          {usage?.teachersMax === -1 ? 'Illimité' : `${getPercent(usage?.teachersUsed || 0, usage?.teachersMax || 0)}% utilisé`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Users Licenses */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#faf5ff', color: '#9333ea' }}>
                              <AdminPanelSettingsIcon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('subscription.quota.users', 'Utilisateurs & Staff')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('subscription.quota.usersSub', 'Comptes d’accès')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            {usage?.usersUsed || 0}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              / {usage?.usersMax === -1 ? '∞' : usage?.usersMax}
                            </Typography>
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={getPercent(usage?.usersUsed || 0, usage?.usersMax || 0)}
                          color={getProgressColor(usage?.usersUsed || 0, usage?.usersMax || 0)}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                          {usage?.usersMax === -1 ? 'Illimité' : `${getPercent(usage?.usersUsed || 0, usage?.usersMax || 0)}% utilisé`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Agencies */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fff7ed', color: '#ea580c' }}>
                              <DomainIcon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('subscription.quota.agencies', 'Agences & Succursales')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('subscription.quota.agenciesSub', 'Centres de formation')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            {usage?.agenciesUsed || 1}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              / {usage?.agenciesMax === -1 ? '∞' : usage?.agenciesMax}
                            </Typography>
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={getPercent(usage?.agenciesUsed || 1, usage?.agenciesMax || 1)}
                          color={getProgressColor(usage?.agenciesUsed || 1, usage?.agenciesMax || 1)}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                          {usage?.agenciesMax === -1 ? 'Multi-agences Illimité' : `${usage?.agenciesMax} agence(s) incluse(s)`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Storage */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#f0fdfa', color: '#0d9488' }}>
                              <CloudQueueIcon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('subscription.quota.storage', 'Stockage Cloud')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('subscription.quota.storageSub', 'Photos, PDFs & Sauvegardes')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            {usage?.storageUsedMb || 0} Mo
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              / {usage?.storageMaxMb ? `${Math.round(usage.storageMaxMb / 1024)} Go` : '200 Mo'}
                            </Typography>
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={getPercent(usage?.storageUsedMb || 0, usage?.storageMaxMb || 200)}
                          color={getProgressColor(usage?.storageUsedMb || 0, usage?.storageMaxMb || 200)}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                          {getPercent(usage?.storageUsedMb || 0, usage?.storageMaxMb || 200)}% de stockage consommé
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* SMS Credits */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fdf2f8', color: '#db2777' }}>
                              <SmsIcon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('subscription.quota.sms', 'Crédits SMS')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('subscription.quota.smsSub', 'Notifications & Rappels')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            {usage?.smsUsed || 0}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              / {usage?.smsMax || 0}
                            </Typography>
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={getPercent(usage?.smsUsed || 0, usage?.smsMax || 100)}
                          color={getProgressColor(usage?.smsUsed || 0, usage?.smsMax || 100)}
                          sx={{ height: 8, borderRadius: 4, mb: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                          {usage?.smsMax ? `${usage.smsMax} SMS inclus par cycle` : 'Non inclus sur cette formule'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Invoices History Table */}
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="action" />
                  {t('subscription.invoicesTitle', 'Historique des Règlements & Factures')}
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>N° Facture</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Montant</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Moyen de paiement</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Réf Transaction SATIM</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            Aucune facture enregistrée pour le moment.
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map((inv) => (
                          <TableRow key={inv.INV_IDF} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{inv.INV_NUMBER}</TableCell>
                            <TableCell>{inv.INV_DATE}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              {inv.INV_AMOUNT.toLocaleString('fr-DZ')} {inv.INV_CURRENCY}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={inv.INV_PAYMENT_METHOD || 'SATIM'}
                                size="small"
                                icon={<CreditCardIcon sx={{ fontSize: 14 }} />}
                                sx={{ bgcolor: '#f1f5f9' }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {inv.INV_TRANSACTION_ID || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={inv.INV_STATUS === 'PAID' ? 'Payé' : 'En attente'}
                                size="small"
                                color={inv.INV_STATUS === 'PAID' ? 'success' : 'warning'}
                                sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}

          {/* TAB 2: PLANS & PRICING */}
          {activeTab === 'plans' && (
            <Box>
              {/* Billing Cycle Switcher */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {t('subscription.pricingTitle', 'Choisissez le forfait adapté à votre académie')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  {t('subscription.pricingSubtitle', 'Paiement direct et sécurisé par carte CIB ou Edahabia (SATIM). Activation instantanée.')}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    display: 'inline-flex',
                    p: 0.5,
                    borderRadius: 3,
                    bgcolor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Button
                    variant={billingCycle === 'monthly' ? 'contained' : 'text'}
                    color={billingCycle === 'monthly' ? 'primary' : 'inherit'}
                    onClick={() => setBillingCycle('monthly')}
                    sx={{
                      borderRadius: 2.5,
                      px: 3,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: billingCycle === 'monthly' ? 2 : 0,
                    }}
                  >
                    {t('subscription.monthly', 'Facturation Mensuelle')}
                  </Button>
                  <Button
                    variant={billingCycle === 'yearly' ? 'contained' : 'text'}
                    color={billingCycle === 'yearly' ? 'primary' : 'inherit'}
                    onClick={() => setBillingCycle('yearly')}
                    sx={{
                      borderRadius: 2.5,
                      px: 3,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: billingCycle === 'yearly' ? 2 : 0,
                    }}
                  >
                    {t('subscription.yearly', 'Facturation Annuelle')}
                    <Chip
                      label="-20%"
                      size="small"
                      color="success"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                    />
                  </Button>
                </Paper>
              </Box>

              {/* Plans Grid */}
              <Grid container spacing={3} alignItems="stretch">
                {plans.map((plan) => {
                  const isCurrent = plan.PLN_CODE === currentPlanCode;
                  const isPopular = plan.PLN_CODE === 'PRO';
                  const price =
                    billingCycle === 'yearly' ? plan.PLN_PRICE_YEARLY : plan.PLN_PRICE_MONTHLY;

                  // Parse feature list
                  let featureList: string[] = [];
                  try {
                    featureList = JSON.parse(plan.PLN_FEATURES_JSON || '[]');
                  } catch {
                    featureList = [];
                  }

                  return (
                    <Grid item xs={12} sm={6} lg={3} key={plan.PLN_IDF}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 3,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          border: isPopular ? '2px solid #6366f1' : '1px solid #e2e8f0',
                          boxShadow: isPopular ? '0 12px 28px rgba(99, 102, 241, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        {isPopular && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 14,
                              right: 14,
                              bgcolor: '#6366f1',
                              color: '#fff',
                              px: 1.5,
                              py: 0.25,
                              borderRadius: 2,
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            ⭐ {plan.PLN_BADGE || 'Populaire'}
                          </Box>
                        )}

                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ color: plan.PLN_COLOR || '#0f172a' }}>
                            {plan.PLN_NAME}
                          </Typography>

                          <Typography variant="caption" color="text.secondary" sx={{ minHeight: 36, display: 'block', mb: 2 }}>
                            {plan.PLN_DESCRIPTION}
                          </Typography>

                          {/* Price */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h4" fontWeight="bold" component="span">
                              {price === 0 ? 'Gratuit' : price.toLocaleString('fr-DZ')}
                            </Typography>
                            {price > 0 && (
                              <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                                {plan.PLN_CURRENCY} / {billingCycle === 'yearly' ? 'an' : 'mois'}
                              </Typography>
                            )}
                          </Box>

                          {plan.PLN_TRIAL_DAYS > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Chip
                                label={`${plan.PLN_TRIAL_DAYS} jours d'essai gratuit`}
                                size="small"
                                sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 600 }}
                              />
                            </Box>
                          )}

                          <Divider sx={{ my: 1.5 }} />

                          {/* Core Quotas Specs */}
                          <Stack spacing={1.2} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2">
                                <b>{plan.PLN_MAX_STUDENTS === -1 ? 'Apprenants illimités' : `${plan.PLN_MAX_STUDENTS} apprenants`}</b>
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2">
                                <b>{plan.PLN_MAX_TEACHERS === -1 ? 'Enseignants illimités' : `${plan.PLN_MAX_TEACHERS} enseignants`}</b>
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2">
                                <b>{plan.PLN_MAX_USERS === -1 ? 'Comptes staff illimités' : `${plan.PLN_MAX_USERS} compte(s) staff`}</b>
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2">
                                <b>{plan.PLN_MAX_AGENCIES === -1 ? 'Multi-Agences illimité' : `${plan.PLN_MAX_AGENCIES} agence incluse`}</b>
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2">
                                <b>{plan.PLN_STORAGE_MB >= 1024 ? `${Math.round(plan.PLN_STORAGE_MB / 1024)} Go` : `${plan.PLN_STORAGE_MB} Mo`}</b> de stockage cloud
                              </Typography>
                            </Box>

                            {plan.PLN_SMS_CREDITS > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                <Typography variant="body2">
                                  <b>{plan.PLN_SMS_CREDITS} SMS</b> inclus / cycle
                                </Typography>
                              </Box>
                            )}

                            {plan.PLN_REPORTS_ENABLED && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                <Typography variant="body2">
                                  Exports & Rapports PDF / Excel
                                </Typography>
                              </Box>
                            )}

                            {plan.PLN_CODE === 'PRO' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                <Typography variant="body2">
                                  Système Quiz, Badges & Remises
                                </Typography>
                              </Box>
                            )}

                            {plan.PLN_CODE === 'ENTERPRISE' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                <Typography variant="body2">
                                  Support VIP 24/7 & API dédiée
                                </Typography>
                              </Box>
                            )}
                          </Stack>

                          {/* CTA Button */}
                          <Box sx={{ mt: 'auto' }}>
                            {isCurrent ? (
                              <Button
                                variant="outlined"
                                color="inherit"
                                fullWidth
                                disabled
                                sx={{ fontWeight: 'bold', py: 1 }}
                              >
                                {t('subscription.currentPlan', 'Forfait Actuel')}
                              </Button>
                            ) : (
                              <Button
                                variant={isPopular ? 'contained' : 'outlined'}
                                color={isPopular ? 'primary' : 'inherit'}
                                fullWidth
                                onClick={() => handleOpenCheckout(plan)}
                                startIcon={<CreditCardIcon />}
                                sx={{
                                  fontWeight: 'bold',
                                  py: 1,
                                  ...(isPopular
                                    ? {
                                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                      }
                                    : {}),
                                }}
                              >
                                {price === 0
                                  ? t('subscription.chooseFree', 'Choisir Gratuit')
                                  : t('subscription.payWithSatim', 'Payer par SATIM')}
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* SATIM Trust Banner */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  mt: 4,
                  borderRadius: 3,
                  bgcolor: '#f8fafc',
                  textAlign: 'center',
                  borderColor: '#e2e8f0',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  🔒 Paiement 100% Sécurisé Garanti par SATIM
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto', mb: 2 }}>
                  Toutes les transactions par carte CIB et Edahabia sont protégées par le protocole d'authentification forte 3D-Secure avec validation par code SMS unique. Aucune donnée bancaire n'est stockée sur nos serveurs.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label="Carte CIB" icon={<CreditCardIcon />} sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 'bold' }} />
                  <Chip label="Carte Edahabia (Poste)" icon={<CreditCardIcon />} sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 'bold' }} />
                  <Chip label="Certification PCI-DSS / SATIM" icon={<SecurityIcon />} sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 'bold' }} />
                </Box>
              </Paper>
            </Box>
          )}

          {/* SATIM Modal */}
          <SatimPaymentModal
            open={satimModalOpen}
            onClose={() => setSatimModalOpen(false)}
            plan={selectedPlanForPayment}
            billingCycle={billingCycle}
            onSuccess={handlePaymentSuccess}
          />
        </Box>
      )}
    </GenericPage>
  );
};

export default SubscriptionPage;
